import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { curiositiesData, CuriosityData } from '../../data/curiositiesData';

interface Curiosity {
  id: string;
  name: string;
  image: string;
  link: string;
}

const BrowseCuriosities: React.FC = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Generate curiosities dynamically from curiositiesData
  const getCuriosities = (): Curiosity[] => {
    return Object.values(curiositiesData).map((curiosityData: CuriosityData) => {
      return {
        id: curiosityData.id,
        name: t(`landing.curiosities.${curiosityData.id}.title`),
        image: curiosityData.image,
        link: curiosityData.link
      };
    });
  };

  const curiosities = getCuriosities();
  
  // Compute slides count (3 cards per slide)
  const slidesCount = Math.max(1, Math.ceil(curiosities.length / 3));
  const navDisabled = slidesCount === 1;

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(curiosities.length / 3));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.ceil(curiosities.length / 3)) % Math.ceil(curiosities.length / 3));
  };

  const renderCuriosityCard = (curiosity: Curiosity) => (
    <div key={curiosity.id} className="col flex-1 min-w-0">
      <div className="img-holder rounded-lg overflow-hidden shadow-lg">
        <Link to={curiosity.link}>
          <img 
            src={curiosity.image} 
            alt={curiosity.name}
            className="w-full h-64 object-cover transition-transform duration-300 hover:scale-105"
          />
        </Link>
      </div>
      <h2 className="curiosities-title text-center mt-4">
        <Link 
          to={curiosity.link} 
          className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
        >
          {curiosity.name}
        </Link>
      </h2>
    </div>
  );

  return (
    <section id="curiosities" className="curiosities-section py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <header className="section-header text-center mb-12">
          <h2 className="section-title text-4xl font-bold text-gray-900 mb-4">
            {t('landing.curiosities.title')}
          </h2>
          <div className="section-content">
            <p className="text-gray-600 text-lg">
              {t('landing.curiosities.subtitle')}
            </p>
          </div>
        </header>

        {/* Mobile: List View */}
        {isMobile && (
          <div className="grid grid-cols-1 gap-8 max-w-4xl mx-auto">
            {curiosities.map(renderCuriosityCard)}
          </div>
        )}

        {/* Desktop/Tablet: Slider View */}
        {!isMobile && (
          <div className="max-w-7xl mx-auto">
            {/* Slider Container with Side Navigation */}
            <div className="flex items-center gap-4">
              {/* Left Navigation Button */}
              <button
                onClick={prevSlide}
                disabled={navDisabled}
                className={`flex-shrink-0 bg-white text-gray-800 p-4 rounded-full shadow-lg transition-all duration-200 border border-gray-200 ${navDisabled ? 'opacity-0 pointer-events-none' : 'hover:bg-gray-50 hover:scale-110'}`}
                aria-label="Previous curiosities"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Slider Content */}
              <div className="flex-1 overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ 
                    transform: `translateX(-${currentSlide * 100}%)`
                  }}
                >
                  {Array.from({ length: slidesCount }, (_, slideIndex) => {
                    const slideCuriosities = curiosities.slice(slideIndex * 3, slideIndex * 3 + 3);
                    const remainingSlots = 3 - slideCuriosities.length;
                    
                    return (
                      <div key={slideIndex} className="flex gap-8 w-full flex-shrink-0">
                        {slideCuriosities.map(renderCuriosityCard)}
                        {/* Fill remaining slots with empty divs to maintain layout */}
                        {Array.from({ length: remainingSlots }, (_, index) => (
                          <div key={`empty-${index}`} className="col flex-1 min-w-0 opacity-0">
                            <div className="img-holder rounded-lg overflow-hidden shadow-lg">
                              <div className="w-full h-64 bg-transparent"></div>
                            </div>
                            <h2 className="curiosities-title text-center mt-4">
                              <div className="text-2xl font-bold text-transparent">Placeholder</div>
                            </h2>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Navigation Button */}
              <button
                onClick={nextSlide}
                disabled={navDisabled}
                className={`flex-shrink-0 bg-white text-gray-800 p-4 rounded-full shadow-lg transition-all duration-200 border border-gray-200 ${navDisabled ? 'opacity-0 pointer-events-none' : 'hover:bg-gray-50 hover:scale-110'}`}
                aria-label="Next curiosities"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Dots Indicator */}
            {slidesCount > 1 && (
              <div className="flex justify-center mt-8 space-x-2">
                {Array.from({ length: slidesCount }, (_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-4 h-4 rounded-full transition-all duration-200 ${
                      index === currentSlide 
                        ? 'bg-blue-600 scale-125' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BrowseCuriosities;
